import React from "react";
import styles from "./ota-release-notes.module.scss";
import MarkdownViewer from "@src/components/atoms/markdown-viewer/markdown-viewer";
import { OTA_RELEASE_NOTES as cssClassName } from "../../../cloud-connect/ota-update/constants";
import { SubTitle } from "@src/components/atoms/sub-title/sub-title";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { ListItem } from "@src/types/ota-update";
import { useOtaReleaseNotes } from "./use-ota-release-notes";

// Props definition for OTA release notes component
interface OTAReleaseNotesProps {
  cardData: ListItem | null;
  width?: number | string;
  activeOTAHistoryTab?: string;
}

// Renders categorized OTA release notes with optional layout width
const OTAReleaseNotes = ({
  cardData,
  width = "auto",
  activeOTAHistoryTab,
}: OTAReleaseNotesProps) => {
  const { isInModal, textColor, title, sections, infoText } =
    useOtaReleaseNotes({
      cardData,
      width,
      activeOTAHistoryTab,
    });

  return (
    // Root container for OTA release notes
    <div
      className={styles[cssClassName]}
      style={{
        width: typeof width === "number" ? `${width}%` : width,
      }}
    >
      <div className={styles[`${cssClassName}__scroll`]}>
        {/* Section title for release notes */}
        <SubTitle text={title} />

        {/* Show info text when no data is available */}
        {!cardData && (
          <p className={styles[`${cssClassName}__info`]}>{infoText}</p>
        )}

        {/* Spacer between title/info and content */}
        <Spacer16 />

        {/* Introductory release notes description */}
        <div
          className={styles[`${cssClassName}__intro`]}
          style={{
            color: textColor,
          }}
        >
          {/* Render markdown intro content */}
          <MarkdownViewer content={cardData?.release_notes_intro || ""} />
        </div>

        {/* Container for categorized release note sections */}
        <div
          className={`${styles[cssClassName + "__pointers"]} ${
            isInModal
              ? styles[cssClassName + "__pointers--full"]
              : styles[cssClassName + "__pointers--half"]
          }`}
        >
          {sections?.map((section, index) => {
            return (
              <div
                key={`${section.title}-${index}`}
                className={styles[`${cssClassName}__section`]}
              >
                {/* Section title (Improvements, Fixes, General) */}
                <p className={styles[`${cssClassName}__head`]}>
                  {section.title}
                </p>

                {/* Bullet list for section content */}
                <div
                  className={styles[`${cssClassName}__list`]}
                  style={{
                    color: textColor,
                  }}
                >
                  <MarkdownViewer content={section?.content || ""} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OTAReleaseNotes;
